import { useCallback, useMemo, useState } from "react";
import { Plus, Save } from "lucide-react";
import { toast } from "react-toastify";

import { dateToLocale } from "@/services/Utils";
import { hasDateInputValue, toDateInputValue, validateSeasonDates } from "@/pages/Settings/Customer/customer.constants";
import CustomButton from "../desktop/button/CustomButton";
import CustomBadge from "../desktop/badge/CustomBadge";
import SimpleTableCard from "@/components/desktop/list/simple-table/SimpleTableCard";
import SeasonStatusAlerts from "@/pages/Settings/Customer/SeasonStatusAlerts.jsx";

import classes from "./Season.module.scss";

const formatSeasonName = (initial_date, end_date) => {
	const startYear = initial_date.slice(0, 4);
	const endYear = end_date.slice(0, 4);
	return `${startYear} - ${endYear}`;
};

const Season = ({ seasons, setSeasons, handleDelete, alertGroups = [], onSave, isSaving = false, isSaveDisabled = false }) => {
	const [newSeason, setNewSeason] = useState({ initial_date: "", end_date: "" });
	const [showForm, setShowForm] = useState(false);
	const [editingIndex, setEditingIndex] = useState(null);
	const [editData, setEditData] = useState({ initial_date: "", end_date: "" });

	const addSeason = () => {
		const datesValidation = validateSeasonDates(newSeason.initial_date, newSeason.end_date);
		if (!datesValidation.valid) {
			toast.error(datesValidation.message);
			return;
		}

		const name = formatSeasonName(newSeason.initial_date, newSeason.end_date);
		const updatedSeasons = seasons.map((season) => ({ ...season, active: false }));

		const allSeasons = [
			...updatedSeasons,
			{
				name,
				initial_date: newSeason.initial_date,
				end_date: newSeason.end_date,
				active: true,
			},
		];

		allSeasons.sort((firstSeason, secondSeason) => new Date(secondSeason.initial_date) - new Date(firstSeason.initial_date));
		setSeasons(allSeasons);

		setNewSeason({ initial_date: "", end_date: "" });
		setShowForm(false);
	};

	const toggleActive = useCallback(
		(index) => {
			const updated = seasons.map((season, seasonIndex) => ({
				...season,
				active: seasonIndex === index,
			}));
			setSeasons(updated);
		},
		[seasons, setSeasons],
	);

	const startEdit = useCallback(
		(index) => {
			const season = seasons[index];
			setEditingIndex(index);
			setEditData({
				initial_date: toDateInputValue(season.initial_date),
				end_date: toDateInputValue(season.end_date),
			});
		},
		[seasons],
	);

	const cancelEdit = useCallback(() => {
		setEditingIndex(null);
		setEditData({ initial_date: "", end_date: "" });
	}, []);

	const saveEdit = useCallback(
		(index) => {
			const datesValidation = validateSeasonDates(editData.initial_date, editData.end_date);
			if (!datesValidation.valid) {
				toast.error(datesValidation.message);
				return;
			}

			const updatedSeasons = [...seasons];
			const name = formatSeasonName(editData.initial_date, editData.end_date);
			updatedSeasons[index] = {
				...updatedSeasons[index],
				initial_date: editData.initial_date,
				end_date: editData.end_date,
				name,
			};

			updatedSeasons.sort((firstSeason, secondSeason) => new Date(secondSeason.initial_date) - new Date(firstSeason.initial_date));
			setSeasons(updatedSeasons);
			cancelEdit();
		},
		[seasons, editData, setSeasons, cancelEdit],
	);

	const tableData = useMemo(
		() =>
			seasons.map((season, index) => ({
				...season,
				rowIndex: index,
				rowKey: season.id || `season-${index}`,
			})),
		[seasons],
	);

	const columns = useMemo(
		() => [
			{
				key: "name",
				label: "Nombre",
				align: "left",
			},
			{
				key: "initial_date",
				label: "Inicio",
				align: "left",
				render: (value, row) =>
					editingIndex === row.rowIndex ? (
						<input
							type="date"
							required
							autoComplete="off"
							value={editData.initial_date}
							onChange={(event) => setEditData((previous) => ({ ...previous, initial_date: event.target.value }))}
							className={classes.dateInput}
						/>
					) : (
						dateToLocale(value)
					),
			},
			{
				key: "end_date",
				label: "Fin",
				align: "left",
				render: (value, row) =>
					editingIndex === row.rowIndex ? (
						<input
							type="date"
							required
							autoComplete="off"
							value={editData.end_date}
							onChange={(event) => setEditData((previous) => ({ ...previous, end_date: event.target.value }))}
							className={classes.dateInput}
						/>
					) : (
						dateToLocale(value)
					),
			},
			{
				key: "active",
				label: "Estado",
				align: "left",
				render: (value, row) => {
					const isActive = Number(value) === 1;

					return (
						<CustomBadge
							variant={isActive ? "success" : "neutral"}
							size="md"
							pill={true}
							onClick={() => !isActive && toggleActive(row.rowIndex)}
							className={!isActive ? classes.clickable : ""}
						>
							{isActive ? "Activa" : "Inactiva"}
						</CustomBadge>
					);
				},
			},
			{
				key: "actions",
				label: "Acciones",
				align: "right",
				render: (_, row) => (
					<div className={classes.actions}>
						{editingIndex === row.rowIndex ? (
							<>
								<CustomButton variant="secondary" size="sm" onClick={cancelEdit}>
									Cancelar
								</CustomButton>
								<CustomButton
									variant="primary"
									size="sm"
									onClick={() => saveEdit(row.rowIndex)}
									disabled={!hasDateInputValue(editData.initial_date) || !hasDateInputValue(editData.end_date)}
								>
									Guardar
								</CustomButton>
							</>
						) : (
							<>
								<CustomButton variant="ghost" size="sm" onClick={() => startEdit(row.rowIndex)}>
									Editar
								</CustomButton>
								<CustomButton variant="danger" size="sm" onClick={() => handleDelete(row.rowIndex)}>
									Eliminar
								</CustomButton>
							</>
						)}
					</div>
				),
			},
		],
		[editingIndex, editData, handleDelete, toggleActive, startEdit, saveEdit, cancelEdit],
	);

	const toolbar = (
		<div className={classes.cardPanel}>
			<SeasonStatusAlerts alertGroups={alertGroups} />

			<div className={classes.toolbar}>
				<div>
					<h2 className={classes.sectionTitle}>Temporadas</h2>
					<p className={classes.sectionSubtitle}>Configure el calendario operativo del cliente</p>
				</div>
				<div className={classes.toolbarActions}>
					{typeof onSave === "function" && (
						<CustomButton variant="primary" size="sm" startIcon={<Save size={16} />} onClick={onSave} disabled={isSaveDisabled} loading={isSaving}>
							Guardar cambios
						</CustomButton>
					)}
					<CustomButton
						variant={showForm ? "secondary" : "primary"}
						size="sm"
						startIcon={!showForm ? <Plus size={16} /> : null}
						onClick={() => setShowForm(!showForm)}
						disabled={isSaveDisabled}
					>
						{showForm ? "Cancelar" : "Nueva temporada"}
					</CustomButton>
				</div>
			</div>

			{showForm && (
				<div className={classes.form}>
					<span className={classes.formLabel}>Inicio</span>
					<span className={classes.formLabel}>Fin</span>
					<span className={classes.formLabelPlaceholder} aria-hidden="true">
						Acción
					</span>
					<input
						type="date"
						required
						autoComplete="off"
						value={newSeason.initial_date}
						onChange={(event) => setNewSeason((previous) => ({ ...previous, initial_date: event.target.value }))}
						className={classes.dateInput}
					/>
					<input
						type="date"
						required
						autoComplete="off"
						value={newSeason.end_date}
						onChange={(event) => setNewSeason((previous) => ({ ...previous, end_date: event.target.value }))}
						className={classes.dateInput}
					/>
					<CustomButton
						variant="primary"
						size="sm"
						className={classes.formConfirmButton}
						onClick={addSeason}
						disabled={!hasDateInputValue(newSeason.initial_date) || !hasDateInputValue(newSeason.end_date)}
					>
						Confirmar
					</CustomButton>
				</div>
			)}
		</div>
	);

	return <SimpleTableCard columns={columns} data={tableData} rowKey="rowKey" emptyMessage="Sin temporadas configuradas" toolbar={toolbar} />;
};

export default Season;
